<template>
  <div v-bind="$attrs">
    <ChooseProviderDialog
      :visible="currentStep === 0 && visible"
      :providers="providerList"
      :selected-provider-id="selectedProviderId"
      :loading="switchingProvider || loadingProviders"
      @close="closeDialog"
      @confirm="handleProviderConfirm"
    />

    <!-- Step 1: Place Order Dialog -->
    <PlaceOrderDialog
      :visible="currentStep === 1 && visible"
      :room-id="roomId"
      :selected-provider-id="selectedProviderId"
      :patient-id="patientId"
      :config-mode="configMode"
      :initial-items="initialItems"
      :initial-is-dbs-version="initialIsDbsVersion"
      :initial-order-items="initialOrderItems"
      @close="closeDialog"
      @checkout="handleCheckout"
    />

    <!-- Step 2: Checkout Dialog -->
    <CheckoutDialog
      :visible="currentStep === 2 && visible"
      :room-id="roomId"
      :cart="checkoutData.cart"
      :is-subscription="checkoutData.isSubscription"
      :patient-id="patientId"
      :customer-id="customerId"
      :is-clinic="isClinic"
      :is-us-customer="isUsCustomer"
      :clinic-id="clinicId"
      :selected-provider-id="selectedProviderId"
      :use-coupon="useCoupon"
      :config-mode="configMode"
      @close="closeDialog"
      @back="goBackToStep1"
      @send="handleSend"
    />
  </div>
</template>

<script>
import { multiLogin } from '@/api/va-order-page/orderTest';
import ChooseProviderDialog from './ChooseProviderDialog.vue';
import PlaceOrderDialog from './PlaceOrderDialog.vue';
import CheckoutDialog from './CheckoutDialog.vue';

export default {
  name: "NewOrderDialog",
  components: {
    ChooseProviderDialog,
    PlaceOrderDialog,
    CheckoutDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    roomId: {
      type: String,
      default: "",
    },
    patientId: {
      type: [String, Number],
      default: "",
    },
    clientId: {
      type: [String, Number],
      default: "",
    },
    customerId: {
      type: Number,
      default: null,
    },
    isClinic: {
      type: Boolean,
      default: false,
    },
    isUsCustomer: {
      type: Boolean,
      default: true,
    },
    clinicId: {
      type: Number,
      default: null,
    },
    useCoupon: {
      type: Boolean,
      default: false,
    },
    configMode: {
      type: Boolean,
      default: false,
    },
    initialItems: {
      type: Array,
      default: () => [],
    },
    initialIsDbsVersion: {
      type: Boolean,
      default: null,
    },
    initialOrderItems: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'send', 'save'],
  data() {
    return {
      currentStep: 0,
      selectedProviderId: "",
      switchingProvider: false,
      loadingProviders: false,
      checkoutData: {
        cart: [],
        isSubscription: false,
        isDbsVersion: null,
      },
    };
  },
  watch: {
    async visible(newVal) {
      if (newVal) {
        await this.initializeProviderStep();
      } else {
        this.resetState();
      }
    },
  },
  computed: {
    userInfo() {
      return this.$store.state.orderTest.user.userInfo || {};
    },
    providerList() {
      return this.$store.state.orderTest.user.customerList || [];
    },
  },
  methods: {
    resetState() {
      this.currentStep = 0;
      this.selectedProviderId = "";
      this.switchingProvider = false;
      this.loadingProviders = false;
      this.checkoutData = {
        cart: [],
        isSubscription: false,
      };
    },
    async initializeProviderStep() {
      this.resetState();
      this.loadingProviders = true;
      try {
        await this.$store.dispatch('orderTest/user/setUserInfo');
        const latestUserInfo = this.$store.state.orderTest.user.userInfo || {};

        if (latestUserInfo.isClinic) {
          await this.$store.dispatch('orderTest/user/setCustomerList');
        }

        const currentProviderId = String(latestUserInfo.customer_id || "");
        const matchedProvider = this.providerList.find(
          (provider) => String(provider.id) === currentProviderId
        );
        if (matchedProvider) {
          this.$store.dispatch('orderTest/user/setCustomer', matchedProvider);
          this.selectedProviderId = String(matchedProvider.id);
        } else if (this.providerList.length) {
          const firstProvider = this.providerList[0];
          this.$store.dispatch('orderTest/user/setCustomer', firstProvider);
          this.selectedProviderId = String(firstProvider.id);
        }

        // In configMode with existing items (edit/reopen), skip provider dialog
        if (this.configMode && (this.initialItems.length > 0 || this.initialOrderItems.length > 0) && this.selectedProviderId) {
          this.currentStep = 1;
        } else {
          this.currentStep = 0;
        }
      } finally {
        this.loadingProviders = false;
      }
    },
    async handleProviderConfirm(providerId) {
      const targetProviderId = String(providerId || "");
      if (!targetProviderId) {
        return;
      }

      this.switchingProvider = true;
      try {
        const selectedProvider = this.providerList.find(
          (provider) => String(provider.id) === targetProviderId
        ) || { id: targetProviderId, name: "" };
        this.$store.dispatch('orderTest/user/setCustomer', selectedProvider);

        const currentProviderId = String(this.$store.state.orderTest.user.userInfo?.customer_id || "");
        if (currentProviderId !== targetProviderId) {
          const res = await multiLogin({ customerId: targetProviderId });
          sessionStorage.removeItem("jwtToken");
          sessionStorage.setItem("jwtToken", res.data);
          await this.$store.dispatch('orderTest/user/setUserInfo');
        }

        this.selectedProviderId = targetProviderId;
        this.currentStep = 1;
      } catch (error) {
        console.error("Failed to switch provider:", error);
      } finally {
        this.switchingProvider = false;
      }
    },
    closeDialog() {
      this.resetState();
      this.$emit("close");
    },
    handleCheckout(data) {
      this.checkoutData = {
        cart: data.cart,
        isSubscription: data.isSubscription,
        isDbsVersion: data.isDbsVersion ?? null,
      };
      this.currentStep = 2;
    },
    goBackToStep1() {
      this.currentStep = 1;
    },
    handleSend(orderResult) {
      if (this.configMode) {
        // In configMode, map cart items to editor format and emit as 'save'
        const items = (orderResult.items || []).map(item => ({
          id: item.id,
          bundleId: item.bundleId || null,
          category: item.category || item.tab,
          name: item.name,
          count: item.quantity || 1,
          price: Number(item.price).toFixed(2),
          subtests: item.subtests || [],
        }));
        this.$emit("save", { items, isDbsVersion: orderResult.isDbsVersion ?? this.checkoutData.isDbsVersion, payload: orderResult.payload || null, orderItems: orderResult.orderItems || [] });
      } else {
        this.$emit("send", orderResult);
      }
      // Don't call closeDialog() here — CheckoutDialog already emits "close"
    },
  },
};
</script>
